import React, { useEffect, useState } from 'react';
import { get } from '../services/session.service';
import { listAll as listSessionPages } from '../services/sessionPage.service';
import {
  close as closeShowing,
  open as openShowing,
  start as startShowing,
  get as getShowing
} from '../services/showing.service';
import { useNavigate, useParams } from 'react-router-dom';
import NotFound from './NotFound';
import {
  SessionDetails,
  SessionPageDetails,
  ShowingDetails
} from '../models/api';
import LoadingSpinner from '../components/LoadingSpinner';
import Button from '../components/Button';
import { blank, show } from '../services/navigation.service';
import DateRender from '../components/DateRender';
import { ReactComponent as EditIcon } from '../assets/edit_large.svg';
import { ReactComponent as ShareIcon } from '../assets/ios_share_large.svg';
import {
  FaChevronLeft,
  FaChevronRight,
  FaCircle,
  FaPauseCircle,
  FaPlayCircle,
  FaSquareFull
} from 'react-icons/fa';
import CountdownTimer from '../components/Timer';
import { ReactComponent as SensorsIcon } from '../assets/sensors.svg';
import { ReactComponent as PlayIcon } from '../assets/play_circle.svg';
import MonitorLabel from '../components/MonitorLabel';
import GenericSettingsMenu from '../components/GenericSettingsMenu';
import ReadMore from '../components/ReadMore';
import SessionSharingDetails from '../components/SessionSharingDetails';

export interface PerformState {
  currentPage: SessionPageDetails;
  isBlank: boolean;
  isEnded: boolean;
}

const PresentShowing: React.FC = () => {
  const [session, setSession] = useState<SessionDetails | null>();
  const [sessionPages, setSessionPages] = useState<SessionPageDetails[]>([]);
  const [activeShowing, setActiveShowing] = useState<ShowingDetails | null>();
  const [loading, setLoading] = useState<boolean | null>();
  const [errors, setErrors] = useState([] as string[]);
  const [loadMessage, setLoadMessage] = useState('');
  const [navigationLoading, setNavigationLoading] =
    useState<SessionPageDetails | null>(null);
  const [showingLoading, setShowingLoading] = useState(false);
  const [showMenu, setShowMenu] = useState(true);
  const [showSharing, setShowSharing] = useState(false);

  const navigate = useNavigate();

  const { sessionReference, showingReference } = useParams();

  const [currentState, setCurrentState] = useState({} as PerformState);

  const isPresenting =
    !currentState.isEnded && !!activeShowing?.presentationStartUtc;

  const getSession = async () => {
    if (!!loading || !sessionReference || !showingReference) {
      return;
    }
    setLoading(true);
    try {
      const [sessionResponse, pagesResponse, showingResponse] =
        await Promise.all([
          get(sessionReference),
          listSessionPages(sessionReference),
          getShowing(sessionReference, showingReference)
        ]);
      if (
        sessionResponse.success &&
        pagesResponse.success &&
        showingResponse.success
      ) {
        setSession(sessionResponse.data);
        setSessionPages(pagesResponse.data?.pages || []);
        setActiveShowing(showingResponse.data);
        if (
          showingResponse.data?.latestSessionPage &&
          pagesResponse.data?.pages
        ) {
          const matchingPage = pagesResponse.data?.pages.find(
            (p) =>
              p.reference ===
                showingResponse.data?.latestSessionPage?.reference &&
              p.pageReference ===
                showingResponse.data?.latestSessionPage?.pageReference
          );
          if (matchingPage) {
            setCurrentState({
              ...currentState,
              currentPage: matchingPage,
              isBlank: showingResponse.data?.latestSessionPageBlank,
              isEnded: !!showingResponse.data.endDateUtc
            });
          }
        } else {
          if (showingResponse.data) {
            setCurrentState({
              ...currentState,
              isBlank: true,
              isEnded: !!showingResponse.data.endDateUtc
            });
          }
        }

        setErrors([]);
      } else {
        setLoadMessage('');
        setErrors([
          ...sessionResponse.errors,
          ...pagesResponse.errors,
          ...showingResponse.errors
        ]);
      }
    } catch (error) {
      setErrors(['There was a problem loading the showing']);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getSession().catch(console.error);
  }, []);

  if (loading === false && (!session || !activeShowing)) {
    return <NotFound />;
  }

  const handleShow = async (sessionPage: SessionPageDetails | null) => {
    if (!activeShowing) {
      setErrors(['No active showing has been selected']);
      return;
    }

    setNavigationLoading(sessionPage);

    if (sessionPage) {
      scrollToItem(sessionPage.reference);

      const result = await show(sessionPage.reference, activeShowing.reference);
      if (result.success) {
        setCurrentState({
          ...currentState,
          currentPage: sessionPage,
          isBlank: false
        });
        setErrors([]);
      } else {
        setLoadMessage('');
        setErrors(['There was a problem']);
      }
    } else {
      if (currentState.isBlank) {
        const result = await show(
          currentState.currentPage.reference,
          activeShowing.reference
        );
        if (result.success) {
          setCurrentState({ ...currentState, isBlank: false });
          setErrors([]);
        } else {
          setLoadMessage('');
          setErrors(['There was a problem']);
        }
      } else {
        if (!currentState.currentPage) {
          return;
        }
        const result = await blank(
          currentState.currentPage.reference,
          activeShowing.reference
        );
        if (result.success) {
          setCurrentState({ ...currentState, isBlank: true });
          setErrors([]);
        } else {
          setLoadMessage('');
          setErrors(['There was a problem']);
        }
      }
    }
    setNavigationLoading(null);
  };

  const findNextPage = (): SessionPageDetails | null => {
    const index = sessionPages.findIndex((p) => p === currentState.currentPage);
    if (index === -1) {
      return sessionPages[0];
    }

    if (index + 1 >= sessionPages.length) {
      return null;
    }

    return sessionPages[index + 1];
  };

  const findPreviousPage = (): SessionPageDetails | null => {
    const index = sessionPages.findIndex((p) => p === currentState.currentPage);
    if (index === -1) {
      return null;
    }

    if (index - 1 < 0) {
      return null;
    }

    return sessionPages[index - 1];
  };

  const showNext = async () => {
    const page = findNextPage();
    if (!page) {
      setErrors(['No next page']);
      return;
    }
    await handleShow(page);
  };

  const showPrevious = async () => {
    const page = findPreviousPage();
    if (!page) {
      setErrors(['No previous page']);
      return;
    }
    await handleShow(page);
  };

  const toggleBlank = async () => {
    await handleShow(null);
  };

  const handleCloseShowing = async () => {
    if (!!loading || showingLoading || !sessionReference || !activeShowing) {
      return;
    }
    setShowingLoading(true);

    const result = await closeShowing(
      sessionReference,
      activeShowing.reference
    );
    if (result.success) {
      setCurrentState({ ...currentState, isEnded: true } as PerformState);
    } else {
      setLoadMessage('');
      setErrors(['There was a problem']);
    }
    setShowingLoading(false);
  };

  const handleReOpenShowing = async () => {
    if (!!loading || showingLoading || !sessionReference || !activeShowing) {
      return;
    }
    setShowingLoading(true);

    const result = await openShowing(sessionReference, activeShowing.reference);
    if (result.success) {
      if (!currentState.isBlank) {
        await toggleBlank();
        setCurrentState({
          ...currentState,
          isEnded: false,
          isBlank: true
        } as PerformState);
      } else {
        setCurrentState({ ...currentState, isEnded: false } as PerformState);
      }
    } else {
      setLoadMessage('');
      setErrors(['There was a problem']);
    }
    setShowingLoading(false);
  };

  const handleStartShowing = async () => {
    if (
      !!loading ||
      showingLoading ||
      !sessionReference ||
      !activeShowing ||
      activeShowing.presentationStartUtc
    ) {
      return;
    }
    setShowingLoading(true);

    const result = await startShowing(
      sessionReference,
      activeShowing.reference
    );
    if (result.success) {
      setActiveShowing(result.data);
      setCurrentState({
        ...currentState,
        isEnded: false,
        isBlank: false
      } as PerformState);
    } else {
      setLoadMessage('');
      setErrors(['There was a problem']);
    }
    setShowingLoading(false);
  };

  const currentPageDetails = sessionPages.find(
    (p) => p === currentState.currentPage
  );

  function smoothScrollTo(targetId: string, duration = 500): void {
    const target = document.getElementById(targetId);
    if (!target) return;

    const container = document.querySelector<HTMLElement>('.content-body');
    if (!container) return;

    const containerHeight = container.clientHeight;
    const targetPosition =
      target.offsetTop -
      container.offsetTop -
      containerHeight / 2 +
      target.clientHeight / 2;
    const startPosition = container.scrollTop;
    const distance = targetPosition - startPosition;
    let startTime: number | null = null;

    function animationStep(currentTime: number): void {
      if (startTime === null) startTime = currentTime;
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);

      const ease =
        progress < 0.5
          ? 2 * progress * progress
          : -1 + (4 - 2 * progress) * progress;

      if (container) {
        container.scrollTop = startPosition + distance * ease;
      } else {
        return;
      }

      if (timeElapsed < duration) {
        requestAnimationFrame(animationStep);
      }
    }

    requestAnimationFrame(animationStep);
  }

  const scrollToItem = (sessionPageReference: string) => {
    const targetId = `item-page-${sessionPageReference}`;
    smoothScrollTo(targetId);
  };

  const getStatusText = () => {
    if (!activeShowing) {
      return '';
    }
    if (
      activeShowing.presentationStartUtc &&
      currentState.isBlank &&
      !currentState.isEnded
    ) {
      return `Event paused`;
    }
    if (activeShowing.presentationStartUtc && !currentState.isEnded) {
      return (
        <>
          {`Live `}
          <CountdownTimer date={activeShowing.presentationStartUtc} />
        </>
      );
    }
    if (currentState.isEnded) {
      return `Event ended`;
    }
    return `Not started`;
  };
  const getStatusClass = () => {
    if (!activeShowing) {
      return '';
    }
    if (currentState.isBlank) {
      return 'status-blank';
    }
    if (activeShowing.presentationStartUtc && !currentState.isEnded) {
      return 'status-active';
    }
    if (currentState.isEnded) {
      return 'status-blank';
    }
    return 'status-blank';
  };
  const getStatusIcon = () => {
    if (!activeShowing) {
      return '';
    }
    if (currentState.isBlank) {
      return <FaSquareFull />;
    }
    if (activeShowing.presentationStartUtc && !currentState.isEnded) {
      return <FaCircle />;
    }
    if (currentState.isEnded) {
      return <FaSquareFull />;
    }
    return <FaSquareFull />;
  };

  return (
    <>
      {loading && <LoadingSpinner />}

      {loading === false && (
        <>
          <div className="present-area">
            <div className={`taskbar${showMenu ? '-full' : '-min'}`}>
              <div className="taskbar-contents">
                <div className="taskbar-contents__header">
                  <h2>{session?.name}</h2>
                  <h4>
                    {activeShowing?.location}{' '}
                    {!activeShowing?.isPreview && (
                      <DateRender dateString={activeShowing?.startDateUtc} />
                    )}
                    {activeShowing?.isPreview && (
                      <span className="preview">Preview</span>
                    )}
                  </h4>
                  <div
                    className="presentation-status"
                    data-testid="presentation-status"
                    role="status"
                  >
                    {activeShowing?.presentationStartUtc
                      ? currentState.isEnded
                        ? 'Ended'
                        : 'Presenting'
                      : 'Ready'}
                  </div>
                  <div className="preview-content">
                    <iframe
                      src={
                        (activeShowing?.isPreview
                          ? (activeShowing.previewLink ?? '')
                          : (activeShowing?.publicLink ?? '')) +
                        '?autoJoin=true'
                      }
                      title="Preview"
                    />
                  </div>
                  <h3>Programme items</h3>
                </div>
                <div className="presentation-pages">
                  {sessionPages.map((p, i) => (
                    <Button
                      className={`presentation-pages__page ${isPresenting && currentPageDetails === p ? 'active' : ''}`}
                      key={p.reference}
                      onClick={() => handleShow(p)}
                      disabled={!isPresenting || currentPageDetails === p}
                    >
                      <>
                        <span
                          className="page-title"
                          data-testid="side-slide-item"
                        >
                          {i + 1}. {p.name}
                        </span>
                        <span className="tag" data-testid="side-slide-status">
                          Presenting
                        </span>
                      </>
                    </Button>
                  ))}
                </div>
              </div>
              <div className="toggle" onClick={() => setShowMenu((p) => !p)}>
                {showMenu && (
                  <>
                    <FaChevronLeft /> Minimise taskbar
                  </>
                )}
                {!showMenu && <FaChevronRight />}
              </div>
            </div>
            <div className="content-area">
              <div className="content-header with-buttons">
                <div className="title-tag">
                  {!showMenu && (
                    <>
                      {activeShowing?.isPreview && (
                        <div className="preview">Preview</div>
                      )}
                    </>
                  )}
                  <h2
                    className={`title-tag__${showMenu ? 'hidden' : 'visible'}`}
                  >
                    {session?.name}
                  </h2>
                  {activeShowing?.isPreview && (
                    <div
                      className={`preview title-tag__${showMenu ? 'hidden' : 'visible'}`}
                    >
                      Preview
                    </div>
                  )}
                  <div className="mobile-only">
                    <GenericSettingsMenu
                      title="Presentation actions"
                      actions={[
                        {
                          text: 'Share',
                          children: (
                            <>
                              <ShareIcon /> Share
                            </>
                          ),
                          action: () => setShowSharing(true)
                        },
                        {
                          text: 'Edit event',
                          children: (
                            <>
                              <EditIcon /> Edit event
                            </>
                          ),
                          action: () =>
                            navigate(`/sessions/${session?.reference}`)
                        }
                      ]}
                    />
                  </div>
                </div>
                <div className="actions with-buttons">
                  <div className="mobile-only">
                    {!isPresenting && !currentState.isEnded && (
                      <Button
                        isLoading={showingLoading}
                        onClick={handleStartShowing}
                        width={169}
                      >
                        Start presentation
                      </Button>
                    )}
                    {isPresenting && (
                      <Button
                        isLoading={showingLoading}
                        onClick={handleCloseShowing}
                        width={107}
                      >
                        End event
                      </Button>
                    )}
                    {!isPresenting && currentState.isEnded && (
                      <Button
                        isLoading={showingLoading}
                        onClick={handleReOpenShowing}
                        width={136}
                      >
                        Reopen event
                      </Button>
                    )}
                  </div>
                  <div
                    className={getStatusClass()}
                    role="status"
                    data-testid="navigation-status"
                  >
                    {getStatusIcon()} <span>{getStatusText()}</span>
                  </div>
                  <Button type="Outline" onClick={() => setShowSharing(true)}>
                    Share
                  </Button>
                  <Button
                    type="Outline"
                    onClick={() => navigate(`/sessions/${session?.reference}`)}
                  >
                    Edit event
                  </Button>
                  {!isPresenting && !currentState.isEnded && (
                    <Button
                      isLoading={showingLoading}
                      onClick={handleStartShowing}
                      width={169}
                    >
                      Start presentation
                    </Button>
                  )}
                  {isPresenting && (
                    <Button
                      isLoading={showingLoading}
                      onClick={handleCloseShowing}
                      width={107}
                    >
                      End event
                    </Button>
                  )}
                  {!isPresenting && currentState.isEnded && (
                    <Button
                      isLoading={showingLoading}
                      onClick={handleReOpenShowing}
                      width={136}
                    >
                      Reopen event
                    </Button>
                  )}
                </div>
              </div>
              <div className="content-body flush">
                <div className="item-list" data-testid="slide-list" role="list">
                  {sessionPages.map((p) => (
                    <div
                      className={`item-page ${currentPageDetails === p && isPresenting ? 'item-page__active' : ''}`}
                      key={p.reference}
                      id={`item-page-${p.reference}`}
                      data-testid="slide-item"
                      role="listitem"
                      data-item-name={p.name}
                    >
                      <div className="item-page__details">
                        <span
                          className="item-page__title"
                          data-testid="slide-title"
                        >
                          {p.name}
                        </span>
                        <span
                          className="item-page__body"
                          data-testid="slide-body"
                        >
                          {p.pageBody ? <ReadMore text={p.pageBody} /> : <> </>}
                        </span>
                      </div>
                      <div
                        className="item-page__status"
                        data-testid="slide-status"
                      >
                        {currentPageDetails === p && isPresenting && (
                          <div className="item-page__presenting">
                            {currentState.isBlank ? (
                              <PlayIcon />
                            ) : (
                              <SensorsIcon />
                            )}
                            {currentState.isBlank ? 'On hold' : 'Presenting'}
                          </div>
                        )}
                        {currentPageDetails !== p && isPresenting && (
                          <Button
                            onClick={() => handleShow(p)}
                            type="Tertiary"
                            isLoading={
                              navigationLoading?.reference === p.reference
                            }
                            loadingText="Loading"
                            loadingTextAfter={true}
                          >
                            <PlayIcon />
                            Present
                          </Button>
                        )}
                        {!isPresenting && (
                          <Button disabled type="Tertiary">
                            <PlayIcon />
                            Present
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="content-footer">
                <div
                  className="actions"
                  role="status"
                  data-testid="connection-status"
                >
                  {showingReference && (
                    <MonitorLabel showingReference={showingReference} />
                  )}
                </div>
                <div className="buttons">
                  <Button
                    type="Secondary"
                    isLoading={false}
                    onClick={showPrevious}
                    disabled={findPreviousPage() === null || !isPresenting}
                  >
                    <FaChevronLeft />
                    <span>Previous section</span>
                    <span className="mobile-only">Previous</span>
                  </Button>
                  <Button
                    type="Outline"
                    isLoading={false}
                    onClick={toggleBlank}
                    disabled={!isPresenting || !currentState.currentPage}
                  >
                    {currentState.isBlank ? (
                      <>
                        <FaPlayCircle />
                        Resume
                      </>
                    ) : (
                      <>
                        <FaPauseCircle />
                        Hold
                      </>
                    )}
                  </Button>
                  <Button
                    type="Secondary"
                    isLoading={false}
                    onClick={showNext}
                    disabled={findNextPage() === null || !isPresenting}
                  >
                    <span>Next section</span>
                    <span className="mobile-only">Next</span>
                    <FaChevronRight />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {showSharing && session && (
            <SessionSharingDetails
              close={() => setShowSharing(false)}
              isPreview={activeShowing?.isPreview}
              session={session}
              previewShowing={activeShowing?.isPreview ? activeShowing : null}
            />
          )}
        </>
      )}
    </>
  );
};

export default PresentShowing;
